<template>
  <div class="account-editor">
    <div class="mt-1">
      <div class="d-flex full-width accounts-container">
        <div class="input-group flex-1 d-block mb-1 min-width-0 flex-col">
          <div class="label-with-tooltip mb-1">
            <span class="field-label">{{ fieldLabel }}:</span>
            <Tooltip v-if="tooltipText" width="300" placement="top">
              <template #trigger>
                <i class="ml-1 fas fa-question-circle"></i>
              </template>
              <template #tooltip>
                <div class="text-center">
                  {{ tooltipText }}
                </div>
              </template>
            </Tooltip>
          </div>
          <sera-input
            ref="accountNameInput"
            name="accountName"
            :value="inputValue"
            :error="!!formError"
            :disabled="inputDisabled"
            @change="handleTextChange"
          />
        </div>
        <div v-if="!saving" class="d-flex flex-shrink-0 align-items-end">
          <SeraButton
            v-if="editing"
            class="ml-2"
            text="Save"
            @click="handleSave"
          />
          <SeraButton
            v-if="editing"
            type="secondary"
            class="ml-1"
            text="Cancel"
            @click="handleCancel"
          />
          <SeraButton
            v-if="!editing"
            class="edit-button ml-2"
            icon="fa-regular fa-pencil"
            text="Edit"
            @click="editAccount"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import SeraButton from "@/components/Button.vue";
import Tooltip from "@/components/Tooltip.vue";
import { injectUseQuickbooksConfig } from "./useQuickbooksConfig";

const props = defineProps({
  companyId: {
    type: Number,
    default: -1,
    required: true
  },
  fieldName: {
    type: String,
    default: "",
    required: true
  },
  fieldLabel: {
    type: String,
    default: "",
    required: true
  },
  tooltipText: {
    type: String,
    default: "",
    required: false
  },
  accountName: {
    type: String,
    default: "",
    required: false
  }
});

const editing = ref(false);
const saving = ref(false);
const accountName = ref(props.accountName);
const accountNameInput = ref<any>(null);
const formError = ref(props.accountName ? "" : "Required");
const { saveAccount } = injectUseQuickbooksConfig();

function editAccount() {
  editing.value = true;
  setTimeout(() => {
    accountNameInput.value.focus();
  });
}

const inputDisabled = computed(() => {
  return saving.value || !editing.value;
});

const inputValue = computed(() => {
  if (saving.value) {
    return "Saving...";
  }

  if (accountName.value) {
    return accountName.value;
  }

  return editing.value ? "" : "None";
});

function handleTextChange({ detail }) {
  accountName.value = detail;
}

async function handleSave() {
  saving.value = true;
  formError.value = "";

  try {
    const company = await saveAccount(
      props.companyId,
      props.fieldName,
      accountName.value
    );

    accountName.value = company[`${props.fieldName}_name`];

    window.notify.success(`${props.fieldLabel} saved successfully!`);

    editing.value = false;
  } catch (e: any) {
    formError.value = e.message;
  }
  saving.value = false;
}

function handleCancel() {
  formError.value = props.accountName ? "" : "Required";
  accountName.value = props.accountName;
  editing.value = false;
}
</script>

<style lang="scss" scoped>
.account-editor {
  .field-label {
    font-weight: 600;
    font-size: 14px;
  }

  .accounts-container {
    align-items: flex-end;
  }

  .min-width-0 {
    min-width: 0;
  }

  .info {
    color: var(--sera-color-core-blue-mid-blue-500);
  }
}
</style>
